import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API });
export const getToken = () => localStorage.getItem("dcf_token");
export const getCompany = () => localStorage.getItem("dcf_company") || "";

api.interceptors.request.use((c) => {
  const t = getToken();
  if (t) c.headers.Authorization = `Bearer ${t}`;
  if (getCompany()) c.headers["X-Company"] = getCompany();
  return c;
});
api.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response?.status === 403 && e.config.method === "get" && /^\/(suppliers|orders|invoices|assets|fixed-assets|contracts|budgets)$/.test(e.config.url)) {
      return Promise.resolve({ data: [], status: 200 });
    }
    if (e.response?.status === 401 && !e.config.url.includes("/auth/login")) {
      localStorage.removeItem("dcf_token");
      if (window.location.pathname !== "/login") window.location.href = "/login";
    }
    return Promise.reject(e);
  }
);

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Une erreur est survenue.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => `${(x.loc || []).slice(-1)[0] || ""} : ${x.msg}`).join(" · ");
  return d.msg || String(d);
}

export const fileUrl = (id, page) => `${API}/files/${id}${page ? `/pages/${page}` : ""}?t=${getToken()}`;

export async function download(url, body, method = "post") {
  const r = await api.request({ url, method, data: body, responseType: "blob" });
  const cd = r.headers["content-disposition"] || "";
  const name = decodeURIComponent((cd.match(/filename="?([^"]+)"?/) || [])[1] || "export.xlsx");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

export async function openBlob(url) {
  const r = await api.get(url, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
}

const EUR = new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" });
const NUM = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
export const eur = (v) => EUR.format(Number(v) || 0);
export const num = (v) => NUM.format(Number(v) || 0);
export const fdate = (s) => (s ? String(s).slice(0, 10).split("-").reverse().join("/") : "—");
export const fdatetime = (s) => (s ? `${fdate(s)} ${String(s).slice(11, 16)}` : "—");
export const todayIso = () => new Date().toISOString().slice(0, 10);

export const TONES = {
  green: "bg-[#2E7D5B] text-white",
  navy: "bg-[#16163F] text-white",
  gold: "bg-[#A5894E] text-[#16163F]",
  red: "bg-[#B03A3A] text-white",
  gray: "bg-[#6B6B80] text-white",
};
export const INVOICE_STATUS = {
  brouillon: ["Brouillon", "gold"], transmise: ["Transmise à la comptabilité", "navy"], bon_a_payer: ["Bon à payer", "navy"], retournee: ["Retournée au créateur", "gold"],
  payee: ["Payée", "green"], comptabilisee: ["Comptabilisée", "green"], litige: ["En litige", "red"], archivee: ["Archivée", "gray"],
};
export const SCAN_STATUS = { lecture: ["En cours de lecture", "navy"], a_verifier: ["À vérifier", "gold"], erreur: ["Erreur de lecture", "red"] };
export const ORDER_STATUS = {
  brouillon: ["Brouillon", "gray"], envoyee: ["Envoyée", "navy"], livree_partiellement: ["Livrée partiellement", "gold"],
  livree: ["Livrée", "navy"], facturee: ["Facturée", "green"], annulee: ["Annulée", "gray"],
};
export const ASSET_STATUS = { en_stock: ["En stock", "navy"], affecte: ["Affecté", "green"], en_reparation: ["En réparation", "gold"], reforme: ["Réformé", "gray"] };
export const FA_STATUS = { en_service: ["En service", "navy"], totalement_amortie: ["Totalement amortie", "gold"], cedee: ["Cédée", "green"], rebut: ["Mise au rebut", "gray"] };
export const CATEGORIES = { materiel: "Matériel", logiciel: "Logiciel", prestation: "Prestation", telecom: "Télécom" };
export const SOURCES = { manuelle: "Saisie manuelle", scan: "Scannée", email: "Reçue par email", facturx: "Factur-X" };
export const PAY_MODES = { virement: "Virement", carte: "Carte", prelevement: "Prélèvement" };
export const ROLE_LABELS = { createur: "Créateur", comptable: "Comptabilité", superadmin: "Super administrateur" };
export const roleLabel = (u) => u.custom_role_name || ROLE_LABELS[u.role] || u.role;

export const PALETTE = ["#16163F", "#A5894E", "#3A3A7A", "#C9B07F", "#5E5EA0", "#8A6F3A", "#9494C2", "#E2D3B0", "#2A2A5C", "#6B6B80"];
const colorRegistry = {};
export function colorFor(key) {
  if (!(key in colorRegistry)) colorRegistry[key] = PALETTE[Object.keys(colorRegistry).length % PALETTE.length];
  return colorRegistry[key];
}

const PAGE_MODULES = [["/factures-a-traiter", "scans"], ["/fournisseurs", "suppliers"], ["/societes", "companies"], ["/contrats", "contracts"], ["/commandes", "orders"],
  ["/factures", "invoices"], ["/materiel", "assets"], ["/budgets", "budgets"], ["/comptabilite", "accounting"], ["/immobilisations", "fixed-assets"], ["/parametres", "settings"]];
const pagePermOk = (user) => {
  if (!user.custom_role) return true;
  const mod = PAGE_MODULES.find(([p]) => window.location.pathname.startsWith(p))?.[1];
  return !mod || ["creer", "modifier", "valider", "supprimer"].some((a) => (user._perms?.[mod] || []).includes(a));
};
export const can = (user, ...roles) => !!user && !user._readonly && (user.role === "superadmin" || ((roles.includes(user.role) || (roles.includes("admin") && user.role === "comptable")) && pagePermOk(user)));

export function computeDue(dateIso, termCode, terms) {
  if (!dateIso) return "";
  const t = (terms || []).find((x) => x.code === termCode) || { days: 30, eom: true };
  const d = new Date(`${dateIso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + t.days);
  if (t.eom) d.setUTCDate(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate());
  return d.toISOString().slice(0, 10);
}

export const lineTotals = (lines) => {
  let ht = 0, vat = 0;
  (lines || []).forEach((l) => {
    const x = Math.round((Number(l.quantity) || 0) * (Number(l.unit_price) || 0) * 100) / 100;
    ht += x;
    vat += (x * (Number(l.vat_rate) || 0)) / 100;
  });
  const r = (v) => Math.round(v * 100) / 100;
  return { ht: r(ht), vat: r(vat), ttc: r(ht + vat) };
};
